/**
 * Geração de elementos visuais (imagens, títulos, boxes)
 * Funções para gerar elementos visuais do documento LaTeX
 */

import { escapeLatex } from '../core/latex-utils';
import { processarItensComSubitens } from '../processors/latex-item-processor';

/**
 * Verifica se uma imagem é válida (não vazia, não placeholder)
 * @param image Caminho ou URL da imagem
 * @returns true se a imagem é válida
 */
function isImageValid(image: string | undefined | null): boolean {
  if (!image) return false;
  const trimmed = image.trim();
  if (!trimmed || trimmed === '') return false;
  if (trimmed.includes('placeholder')) return false;
  return true;
}

/**
 * Gera o título da parte com número formatado
 * @param numero Número da parte
 * @param texto Nome da parte
 */
export function retProtecao(numero: number, texto: string): string {
  const numeroFormatado = numero < 10 ? `00${numero}` : numero < 100 ? `0${numero}` : String(numero);
  return `\\begin{tcolorbox}[colback=schmersalBlue, colframe=schmersalBlue, sharp corners, fontupper=\\color{white}\\bfseries, boxrule=0.5mm, width=\\textwidth, boxsep=-4pt, left=2mm, right=2mm] ${numeroFormatado} - ${escapeLatex(texto)}\\end{tcolorbox}\n\\vspace{-15pt}\n`;
}

/**
 * Gera uma única imagem centralizada
 * @param image Caminho da imagem
 * @param width Largura da imagem (padrão: 0.9\textwidth)
 * @param scale Escala da imagem (se fornecido, usa scale em vez de width)
 * @param maxWidth Largura máxima da imagem (se fornecido, usa maxwidth em vez de width ou scale)
 */
export function ret1image(image: string, width: string = '0.9\\textwidth', scale?: number, maxWidth?: string): string {
  // Para imagens base64, incluir no LaTeX - o backend vai processar e salvar em arquivos
  const getImagePath = (img: string): string => {
    if (!img || img.includes('placeholder')) {
      return 'placeholder.png';
    }
    // Se for base64, manter a URL completa - backend vai substituir
    // Se for caminho de arquivo, usar diretamente
    return img;
  };

  // Se maxWidth for fornecido, usar width com o valor (limita a largura máxima)
  // Se scale for fornecido, usar scale; caso contrário, usar width
  let sizeParam: string;
  if (maxWidth !== undefined) {
    sizeParam = `width=${maxWidth}`;
  } else if (scale !== undefined) {
    sizeParam = `scale=${scale}`;
  } else {
    // Usar width diretamente - se for passado explicitamente (como '5cm'), será usado
    sizeParam = `width=${width}`;
  }

  return `% Single image aligned to the left
\\noindent\\includegraphics[${sizeParam}]{${escapeLatex(getImagePath(image))}}

`;
}

/**
 * Gera duas imagens lado a lado com a mesma altura
 * @param image1 Caminho da primeira imagem
 * @param image2 Caminho da segunda imagem
 */
export function ret2images(image1: string, image2: string): string {
  // Se as imagens forem placeholders, não incluir
  if (image1.includes('placeholder') && image2.includes('placeholder')) {
    return `% Imagens não disponíveis (placeholders)

`;
  }

  // Para imagens base64, incluir no LaTeX - o backend vai processar e salvar em arquivos
  // Usar um marcador especial que será substituído pelo backend
  const getImagePath = (img: string): string => {
    if (!img || img.includes('placeholder')) {
      return 'placeholder.png';
    }
    // Se for base64, manter a URL completa - backend vai substituir
    // Se for caminho de arquivo, usar diretamente
    return img;
  };

  return `% Two images side by side
\\noindent
\\begin{minipage}[t]{0.45\\linewidth}
    \\includegraphics[height=8.5cm]{${escapeLatex(getImagePath(image1))}}
\\end{minipage}
\\hfill
\\begin{minipage}[t]{0.45\\linewidth}

    \\includegraphics[height=8.5cm]{${escapeLatex(getImagePath(image2))}}
\\end{minipage}
\\par
`;
}

/**
 * Gera três imagens lado a lado
 * @param image1 Caminho da primeira imagem
 * @param image2 Caminho da segunda imagem
 * @param image3 Caminho da terceira imagem (opcional, padrão: "Sil2C3.png")
 */
export function ret3images(image1: string, image2: string, image3: string = "Sil2C3.png"): string {
  // Se as imagens forem placeholders, não incluir
  if (image1.includes('placeholder') && image2.includes('placeholder') && image3.includes('placeholder')) {
    return `% Imagens não disponíveis (placeholders)

`;
  }

  // Para imagens base64, incluir no LaTeX - o backend vai processar e salvar em arquivos
  const getImagePath = (img: string): string => {
    if (!img || img.includes('placeholder')) {
      return 'placeholder.png';
    }
    // Se for base64, manter a URL completa - backend vai substituir
    // Se for caminho de arquivo, usar diretamente
    return img;
  };

  return `\\noindent
\\begin{minipage}[t]{0.3\\linewidth}
    \\includegraphics[height=8.5cm]{${escapeLatex(getImagePath(image1))}}
\\end{minipage}
\\hfill
\\begin{minipage}[t]{0.3\\linewidth}

    \\includegraphics[height=8.5cm]{${escapeLatex(getImagePath(image2))}}
\\end{minipage}
\\hfill
\\begin{minipage}[t]{0.3\\linewidth}

    \\includegraphics[height=8.5cm]{${escapeLatex(getImagePath(image3))}}
\\end{minipage}
\\par
`;
}

/**
 * Gera o ciclo de vida
 * @param ciclos Array de ciclos de vida
 */
export function retCiclo(ciclos: string[]): string {
  // Se o ciclo for apenas números (como "001", "002"), adicionar prefixo "Perigo"
  const ciclosFormatados = ciclos.map(ciclo => {
    // Verificar se é apenas números (pode ter zeros à esquerda)
    const apenasNumeros = /^\d+$/.test(ciclo.trim());
    if (apenasNumeros) {
      return `Perigo ${ciclo.trim()}`;
    }
    return ciclo.trim();
  });

  return `\\textbf{CICLO DE VIDA:} ${ciclosFormatados.join(', ')}
\\vspace{0.5cm}

`;
}

/**
 * Gera o título de perigos (mecânicos ou elétricos)
 * @param perigo Nome do perigo ou array de perigos
 * @param ip Índice do perigo se for array (padrão: 0)
 * @param numeroParte Número da parte para numeração (ex: 1, 2, 3)
 */
export function retPerigo(perigo: string | string[], ip: number = 0, numeroParte: number = 1): string {
  const perigoTexto = Array.isArray(perigo) ? perigo[ip] : perigo;
  return `\\begin{tcolorbox}[colback=schmersalBlue, colframe=schmersalBlue, sharp corners, fontupper=\\color{white}\\bfseries, boxrule=0.5mm, width=\\textwidth, boxsep=-4pt, left=2mm, right=2mm]
${numeroParte}.2. ${escapeLatex(perigoTexto)}
\\end{tcolorbox}`;
}

/**
 * Espaçamento entre perigos
 */
export const spacePerigos = `\\vspace{-5pt} \n\\noindent\n`;

/**
 * Gera o status de conformidade
 * @param image Caminho da imagem
 * @param conforme Valor numérico de conformidade (< 5 = conforme, >= 5 = não conforme)
 */
export function retConformidade(image: string, conforme: number): string {
  if (conforme < 5) {
    return `\\begin{minipage}[c]{0.2\\textwidth}
    % Imagem comentada (não disponível)
    % \\includegraphics[height=1cm, width=1cm]{${escapeLatex(image)}}
\\end{minipage}
\\hfill
\\begin{minipage}[c]{0.35\\textwidth}
    \\textcolor{mygreen}{\\textbf{Em Conformidade}} \\tikz \\draw[mygreen, fill=mygreen] (0,0) circle [radius=0.15cm];
\\end{minipage}

`;
  } else {
    return `\\begin{minipage}[c]{0.2\\textwidth}
    % Imagem comentada (não disponível)
    % \\includegraphics[height=1cm, width=1cm]{${escapeLatex(image)}}
\\end{minipage}
\\hfill
\\begin{minipage}[c]{0.35\\textwidth}
    \\textcolor{red}{\\textbf{NÃO CONFORMIDADE}} \\tikz \\draw[red, fill=white] (0,0) circle [radius=0.15cm];
\\end{minipage}

`;
  }
}

/**
 * Gera o evento perigoso (lesão)
 * @param lesao Texto do evento perigoso ou array
 * @param il Índice se for array (padrão: 0)
 * @param numeroParte Número da parte para numeração (ex: 1, 2, 3)
 */
export function retLesao(lesao: string | string[], il: number = 0, numeroParte: number = 1): string {
  const lesaoTexto = Array.isArray(lesao) ? lesao[il] : lesao;
  return `\\begin{tcolorbox}[colback=gray!30, colframe=gray!30, sharp corners, boxrule=0.5mm, width=\\textwidth, boxsep=-4pt, left=2mm, right=2mm]
\\textbf{${numeroParte}.3. Evento Perigoso:} ${escapeLatex(lesaoTexto)}
\\end{tcolorbox}`;
}

/**
 * Gera perigos encontrados e potenciais consequências lado a lado
 * @param perigos Array de perigos encontrados
 * @param consequencias Array de potenciais consequências
 * @param numeroParte Número da parte para numeração (ex: 1, 2, 3)
 */
export function retPerigosConseq(perigos: string[], consequencias: string[], numeroParte: number): string {
  // Numerar perigos como 1.4.1., 1.4.2., etc.
  const perigosItems = perigos
    .filter(p => p && p.trim())
    .map((p, index) => {
      const numero = `${numeroParte}.4.${index + 1}.`;
      return `\\item[${numero}] ${escapeLatex(p)}`;
    }).join('\n        ');

  // Numerar consequências como 1.5.1., 1.5.2., etc.
  const consequenciasItems = consequencias
    .filter(c => c && c.trim())
    .map((c, index) => {
      const numero = `${numeroParte}.5.${index + 1}.`;
      return `\\item[${numero}] ${escapeLatex(c)}`;
    }).join('\n            ');

  // Se não houver perigos nem consequências, retornar string vazia
  if (!perigosItems && !consequenciasItems) {
    return '';
  }

  // Gerar minipage para perigos encontrados
  let perigosSection = '';
  if (perigosItems) {
    perigosSection = `\\begin{minipage}[t]{0.45\\textwidth}
\\textbf{${numeroParte}.4. Perigos encontrados}
\\begin{itemize}[noitemsep]
        ${perigosItems}
\\end{itemize}
\\end{minipage}`;
  } else {
    perigosSection = `\\begin{minipage}[t]{0.45\\textwidth}
\\textbf{${numeroParte}.4. Perigos encontrados}
\\begin{itemize}[noitemsep]
        \\item Nenhum perigo encontrado.
\\end{itemize}
\\end{minipage}`;
  }

  // Gerar minipage para consequências
  let consequenciasSection = '';
  if (consequenciasItems) {
    consequenciasSection = `\\begin{minipage}[t]{0.45\\textwidth}
    \\textbf{${numeroParte}.5. Potenciais Consequências}
    \\begin{itemize}[noitemsep]
            ${consequenciasItems}
    \\end{itemize}
\\end{minipage}`;
  } else {
    consequenciasSection = `\\begin{minipage}[t]{0.45\\textwidth}
    \\textbf{${numeroParte}.5. Potenciais Consequências}
    \\begin{itemize}[noitemsep]
            \\item Nenhuma consequência especificada.
    \\end{itemize}
\\end{minipage}`;
  }

  return `${perigosSection}
\\hfill
${consequenciasSection}
\\par`;
}

/**
 * Espaçamento entre perigos e consequências
 */
export const spacePerigosConseq = `\\vspace{10pt}\n`;

/**
 * Gera o título para a imagem após correção
 * @param numeroParte Número da parte para numeração (ex: 1, 2, 3)
 */
export function retTituloImagemAposCorrecao(numeroParte: number = 1): string {
  return `\\noindent\\textbf{${numeroParte}.8. Adequações e/ou dispositivos sugeridos}

`;
}

/**
 * Gera apenas a descrição da condição atual
 * @param descricao Array de descrições da condição atual
 * @param conforme Valor numérico de conformidade (< 5 = conforme, >= 5 = não conforme)
 * @param numeroParte Número da parte para numeração (ex: 1, 2, 3)
 */
export function retDescricaoCondicaoAtual(descricao: string[], conforme: number, numeroParte: number): string {
  if (descricao && descricao.length > 0 && descricao[0] !== '') {
    // Processar itens e subitens usando função auxiliar
    const items = processarItensComSubitens(descricao);
    const descricaoItems = items.join('\n            ');

    // Determinar o texto de conformidade
    const textoConformidade = conforme < 5
      ? `\\textcolor{mygreen}{\\textbf{Conforme}} \\tikz \\draw[mygreen, fill=mygreen] (0,0) circle [radius=0.15cm];`
      : `\\textcolor{red}{\\textbf{NÃO CONFORMIDADE}} \\tikz \\draw[red, fill=white] (0,0) circle [radius=0.15cm];`;

    return `
% Descrição da condição atual
\\begin{minipage}[c]{0.65\\textwidth}
\\textbf{${numeroParte}.1. Descrição da condição atual:}
\\end{minipage}
\\hfill
\\begin{minipage}[c]{0.32\\textwidth}
${textoConformidade}
\\end{minipage}
\\par
\\vspace{5pt}
\\begin{itemize}

            ${descricaoItems}
\\end{itemize}

`;
  }
  return '';
}

/**
 * Gera a legenda após a imagem após correção
 * @param legenda Texto da legenda (string)
 */
export function retLegenda(legenda: string): string {
  if (legenda && legenda.trim() !== '') {
    return `\\textbf{Legenda:} ${escapeLatex(legenda.trim())}

`;
  }
  return '';
}

/**
 * Gera medidas de controle, exigências normativas e adequações administrativas (sem descrição da condição atual)
 * @param controle Array de medidas de controle
 * @param normativas Array de exigências normativas
 * @param administrativas Array de adequações administrativas
 */
export function retContNorm(
  controle: string[],
  normativas: string[],
  administrativas: string[]
): string {
  let retornar = '';

  if (controle && controle.length > 0 && controle[0] !== '') {
    const itemsProcessados = processarItensComSubitens(controle);
    const controleItems = itemsProcessados.join('\n            ');
    retornar += `% Medidas de controle
\\textbf{Medidas de controle:}
\\begin{itemize}
            ${controleItems}
\\end{itemize}
`;
  }

  if (normativas && normativas.length > 0 && normativas[0] !== '') {
    const itemsProcessados = processarItensComSubitens(normativas);
    const normativasItems = itemsProcessados.join('\n            ');
    retornar += `% Exigências Normativas
\\textbf{Exigências Normativas:}

\\begin{itemize}
            ${normativasItems}
\\end{itemize}`;
  }

  if (administrativas && administrativas.length > 0 && administrativas[0] !== '') {
    const itemsProcessados = processarItensComSubitens(administrativas);
    const administrativasItems = itemsProcessados.join('\n            ');
    retornar += `

\\textbf{Adequações administrativas}

\\begin{itemize}
            ${administrativasItems}
\\end{itemize}`;
  }

  return retornar;
}

/**
 * Gera descrição da condição atual, medidas de controle, exigências normativas e adequações administrativas
 * @param descricao Array de descrições da condição atual
 * @param controle Array de medidas de controle
 * @param normativas Array de exigências normativas
 * @param administrativas Array de adequações administrativas
 * @param conforme Valor numérico de conformidade (< 5 = conforme, >= 5 = não conforme)
 */
export function retDescContNorm(
  descricao: string[],
  controle: string[],
  normativas: string[],
  administrativas: string[],
  conforme: number
): string {
  let retornar = '';

  if (descricao && descricao.length > 0 && descricao[0] !== '') {
    const descricaoItemsFiltrados = descricao.filter(d => d && d.trim());
    if (descricaoItemsFiltrados.length > 0) {
      const itemsProcessados = processarItensComSubitens(descricaoItemsFiltrados);
      const descricaoItems = itemsProcessados.join('\n            ');

      // Determinar o texto de conformidade
      const textoConformidade = conforme < 5
        ? `\\textcolor{mygreen}{\\textbf{Conforme}} \\tikz \\draw[mygreen, fill=mygreen] (0,0) circle [radius=0.15cm];`
        : `\\textcolor{red}{\\textbf{NÃO CONFORMIDADE}} \\tikz \\draw[red, fill=white] (0,0) circle [radius=0.15cm];`;

      retornar += `% Descrição da condição atual
\\begin{minipage}[c]{0.65\\textwidth}
\\textbf{Descrição da condição atual:}
\\end{minipage}
\\hfill
\\begin{minipage}[c]{0.32\\textwidth}
${textoConformidade}
\\end{minipage}
\\par
\\vspace{5pt}
\\begin{itemize}

            ${descricaoItems}
\\end{itemize}

`;
    }
  }

  if (controle && controle.length > 0 && controle[0] !== '') {
    const controleItemsFiltrados = controle.filter(c => c && c.trim());
    if (controleItemsFiltrados.length > 0) {
      const itemsProcessados = processarItensComSubitens(controleItemsFiltrados);
      const controleItems = itemsProcessados.join('\n            ');
      retornar += `% Medidas de controle
\\textbf{Medidas de controle:}
\\begin{itemize}
            ${controleItems}
\\end{itemize}
`;
    }
  }

  if (normativas && normativas.length > 0 && normativas[0] !== '') {
    const normativasItemsFiltrados = normativas.filter(n => n && n.trim());
    if (normativasItemsFiltrados.length > 0) {
      const itemsProcessados = processarItensComSubitens(normativasItemsFiltrados);
      const normativasItems = itemsProcessados.join('\n            ');
      retornar += `% Exigências Normativas
\\textbf{Exigências Normativas:}

\\begin{itemize}
            ${normativasItems}
\\end{itemize}`;
    }
  }

  if (administrativas && administrativas.length > 0 && administrativas[0] !== '') {
    const administrativasItemsFiltrados = administrativas.filter(a => a && a.trim());
    if (administrativasItemsFiltrados.length > 0) {
      const itemsProcessados = processarItensComSubitens(administrativasItemsFiltrados);
      const administrativasItems = itemsProcessados.join('\n            ');
      retornar += `

\\textbf{Adequações administrativas}

\\begin{itemize}
            ${administrativasItems}
\\end{itemize}`;
    }
  }

  return retornar;
}

/**
 * Gera combinação de perigos elétricos e mecânicos
 * @param perigos1 Primeiro tipo de perigo
 * @param perigos2 Segundo tipo de perigo
 */
export function retCombEletricosemec(perigos1: string, perigos2: string): string {
  return `\\begin{tcolorbox}[colback=schmersalBlue, colframe=schmersalBlue, sharp corners, fontupper=\\color{white}\\bfseries, boxrule=0.5mm, width=\\textwidth, boxsep=-4pt, left=2mm, right=2mm]
Combinação de ${escapeLatex(perigos1)} e ${escapeLatex(perigos2)}
\\end{tcolorbox}`;
}

/**
 * Quebra de página
 */
export const nextPage = `.\n\\newpage\n\\clearpage\n`;

/**
 * Exporta função de validação de imagem para uso externo
 */
export { isImageValid };


